import { Control } from 'ol/control';
import { easeOut } from 'ol/easing';
import EventType from 'ol/events/EventType';
import Interaction from 'ol/interaction/Interaction';
import type Map from 'ol/Map';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import type { Coordinate } from 'ol/coordinate';

export const normalZoomStep = 1;
export const precisionZoomStep = 0.125;

const wheelEventGap = 80;
const trackpadEventGap = 400;
const trackpadThreshold = 4;
const precisionTrackpadDeltaPerZoom = 800;

export const zoomStepForModifier = (ctrlKey: boolean): number =>
  ctrlKey ? precisionZoomStep : normalZoomStep;

export const normalizedWheelDelta = (deltaY: number, deltaMode: number): number => {
  if (!Number.isFinite(deltaY)) return 0;
  if (deltaMode === WheelEvent.DOM_DELTA_LINE) return deltaY * 40;
  if (deltaMode === WheelEvent.DOM_DELTA_PAGE) return deltaY * 300;
  return deltaY;
};

export const isTrackpadWheelDelta = (deltaY: number, deltaMode: number): boolean =>
  Math.abs(normalizedWheelDelta(deltaY, deltaMode)) < trackpadThreshold;

const zoomByStep = (map: Map, delta: number): void => {
  const view = map.getView();
  const currentZoom = view.getZoom();
  if (currentZoom === undefined) return;
  if (view.getAnimating()) view.cancelAnimations();
  view.animate({
    zoom: view.getConstrainedZoom(currentZoom + delta),
    duration: 250,
    easing: easeOut,
  });
};

export const precisionWheelDelta = (deltaY: number, deltaMode: number): number => {
  const delta = normalizedWheelDelta(deltaY, deltaMode);
  if (delta === 0) return 0;
  if (Math.abs(delta) < trackpadThreshold) return -delta / precisionTrackpadDeltaPerZoom;
  return -Math.sign(delta) * precisionZoomStep;
};

export class PrecisionWheelZoom extends Interaction {
  private controlKeyPressed = false;
  private ownerDocument: Document | null = null;
  private ownerWindow: Window | null = null;
  private wheelDelta = 0;
  private wheelAnchor: Coordinate | undefined;
  private wheelTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private trackpadTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private trackpadActive = false;
  private mode: 'trackpad' | 'wheel' | undefined;
  private modeStartedAt = 0;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Control') this.controlKeyPressed = true;
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (event.key === 'Control') this.controlKeyPressed = false;
  };

  private readonly onBlur = (): void => { this.controlKeyPressed = false; };

  isPrecisionEvent(event: WheelEvent): boolean {
    return event.ctrlKey && this.controlKeyPressed;
  }

  private clearTimers(): void {
    if (this.wheelTimeoutId !== undefined) clearTimeout(this.wheelTimeoutId);
    if (this.trackpadTimeoutId !== undefined) clearTimeout(this.trackpadTimeoutId);
    this.wheelTimeoutId = undefined;
    this.trackpadTimeoutId = undefined;
  }

  private flushWheel(map: Map): void {
    this.wheelTimeoutId = undefined;
    const delta = precisionWheelDelta(this.wheelDelta, WheelEvent.DOM_DELTA_PIXEL);
    this.wheelDelta = 0;
    if (delta === 0) return;
    const view = map.getView();
    if (view.getAnimating()) view.cancelAnimations();
    view.adjustZoom(delta, this.wheelAnchor);
  }

  private endTrackpad(map: Map): void {
    this.trackpadTimeoutId = undefined;
    if (!this.trackpadActive) return;
    this.trackpadActive = false;
    map.getView().endInteraction(undefined, 0, this.wheelAnchor);
  }

  override setMap(map: Map | null): void {
    this.ownerDocument?.removeEventListener('keydown', this.onKeyDown);
    this.ownerDocument?.removeEventListener('keyup', this.onKeyUp);
    this.ownerWindow?.removeEventListener('blur', this.onBlur);
    this.clearTimers();
    this.wheelDelta = 0;
    this.wheelAnchor = undefined;
    this.trackpadActive = false;
    this.mode = undefined;
    this.controlKeyPressed = false;
    super.setMap(map);
    if (!map) { this.ownerDocument = null; this.ownerWindow = null; return; }
    this.ownerDocument = map.getOwnerDocument();
    this.ownerWindow = this.ownerDocument.defaultView;
    this.ownerDocument.addEventListener('keydown', this.onKeyDown);
    this.ownerDocument.addEventListener('keyup', this.onKeyUp);
    this.ownerWindow?.addEventListener('blur', this.onBlur);
  }

  override handleEvent(mapBrowserEvent: MapBrowserEvent<WheelEvent>): boolean {
    if (mapBrowserEvent.type !== EventType.WHEEL) return true;
    const event = mapBrowserEvent.originalEvent;
    // Chrome reports trackpad pinch as ctrlKey=true without a physical Control key.
    // Let OpenLayers keep handling that gesture with its native smooth pinch path.
    if (!this.isPrecisionEvent(event)) return true;
    event.preventDefault();
    const normalizedDelta = normalizedWheelDelta(event.deltaY, event.deltaMode);
    if (normalizedDelta === 0) return false;
    const now = Date.now();
    if (!this.mode || now - this.modeStartedAt > trackpadEventGap) {
      this.mode = Math.abs(normalizedDelta) < trackpadThreshold ? 'trackpad' : 'wheel';
      this.modeStartedAt = now;
    }
    this.wheelAnchor = mapBrowserEvent.coordinate;
    const view = mapBrowserEvent.map.getView();

    if (this.mode === 'trackpad') {
      if (this.trackpadTimeoutId !== undefined) clearTimeout(this.trackpadTimeoutId);
      if (!this.trackpadActive) {
        if (view.getAnimating()) view.cancelAnimations();
        view.beginInteraction();
        this.trackpadActive = true;
      }
      view.adjustZoom(-normalizedDelta / precisionTrackpadDeltaPerZoom, this.wheelAnchor);
      this.trackpadTimeoutId = setTimeout(() => this.endTrackpad(mapBrowserEvent.map), wheelEventGap);
      this.modeStartedAt = now;
      return false;
    }

    this.wheelDelta += normalizedDelta;
    if (this.wheelTimeoutId !== undefined) clearTimeout(this.wheelTimeoutId);
    this.wheelTimeoutId = setTimeout(() => this.flushWheel(mapBrowserEvent.map), wheelEventGap);
    return false;
  }
}

export const shouldUseNativeWheelZoom = (interaction: PrecisionWheelZoom, event: WheelEvent): boolean =>
  !interaction.isPrecisionEvent(event);

export class PrecisionZoomControl extends Control {
  constructor() {
    const element = document.createElement('div');
    element.className = 'ol-zoom ol-unselectable ol-control';
    const createButton = (label: string, className: string, direction: 1 | -1): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = className;
      button.textContent = label;
      button.title = `${direction > 0 ? 'Acercar' : 'Alejar'} · Ctrl + clic: paso fino · Ctrl + rueda: zoom preciso`;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const map = this.getMap();
        if (map) zoomByStep(map, direction * zoomStepForModifier(event.ctrlKey));
      });
      return button;
    };
    element.append(createButton('+', 'ol-zoom-in', 1), createButton('–', 'ol-zoom-out', -1));
    super({ element });
  }
}
